/**
 * Onglets — une rangée de choix dont UN est actif, en deux styles :
 *
 *   - `segments` : le rail de verre gris des captures (« Semaine · Mois ·
 *     3 mois · Année »), le segment actif en pilule contrastée (blanche la
 *     nuit, noire le jour) qui GLISSE d'une option à l'autre.
 *   - `ligne` : des onglets sous une hairline, l'actif en texte plein et
 *     souligné d'un trait à la marque qui glisse (les onglets d'un panneau,
 *     ceux des Réglages).
 *
 *   <Onglets options={[{ valeur: "comptes", etiquette: "Comptes", compte: 205 }, …]} valeur={v} onChange={setV} />
 *   <Onglets style="segments" options={PERIODES} valeur={p} onChange={setP} taille="sm" />
 *
 * Sémantique `tablist` / `tab` (ou `radiogroup` / `radio` quand c'est une
 * VALEUR qu'on choisit, `semantique="choix"` — c'est `SelecteurPeriode`).
 * Clavier : ← → ↑ ↓ changent, Début / Fin sautent aux bouts. Générique sur
 * la valeur pour que l'appelant garde son type.
 *
 * Successeur des trois `Segmented`, du `SegmentedControl`, des deux `Pilules`
 * et des onglets `role="tab"` écrits à la main dans le cockpit, et de
 * `SelecteurPeriode` (qui reste, comme un cas de celui-ci). Lot 2 de la
 * refonte du catalogue, 28-10-2026.
 */
import { useId, type KeyboardEvent, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";

import { cn } from "./utils";
import "./lecture.css";

export interface Onglet<V extends string = string> {
  valeur: V;
  etiquette: string;
  /** Une icône de `@dozka/ui/icons`, devant l'étiquette. */
  icone?: ReactNode;
  /** Un nombre à droite de l'étiquette (« Comptes 205 »). */
  compte?: number;
  /** Après l'étiquette et le compte, DANS l'onglet : des pastilles (`Badge`) — les nouvelles, ce qui attend.
   *  Jamais un bouton (l'onglet en est déjà un) : un clic sur la pastille choisit l'onglet. */
  suite?: ReactNode;
  desactive?: boolean;
}

export type StyleOnglets = "segments" | "ligne";
export type TailleOnglets = "sm" | "md" | "lg";

export interface OngletsProps<V extends string = string> {
  options: ReadonlyArray<Onglet<V>>;
  valeur: V;
  onChange: (valeur: V) => void;
  /** `ligne` (défaut) ou `segments`. */
  style?: StyleOnglets;
  taille?: TailleOnglets;
  /** Prend toute la largeur, segments égaux. */
  pleineLargeur?: boolean;
  /** Le nom du groupe pour les lecteurs d'écran. */
  nom?: string;
  /** `onglets` (tablist, défaut) ou `choix` (radiogroup : une valeur, pas une vue). */
  semantique?: "onglets" | "choix";
  className?: string;
}

// L'échelle (CLAUDE.md, « L'échelle ») : le rail, son trait de 1 px et son remplissage compris,
// fait la hauteur d'un contrôle de la même taille — sm 32 (26 + 2 × 2 + 2), md 40 (32 + 2 × 3 + 2),
// lg 48 (40 + 2 × 3 + 2). Mesuré dans le catalogue (BarreDActions), pas déduit.
const SEGMENTS: Record<TailleOnglets, { rail: string; segment: string }> = {
  sm: { rail: "p-[2px]", segment: "h-[26px] px-3 text-[12px]" },
  md: { rail: "p-[3px]", segment: "h-8 px-4 text-[13px]" },
  lg: { rail: "p-[3px]", segment: "h-10 px-5 text-[15px]" },
};
const LIGNE: Record<TailleOnglets, { onglet: string }> = {
  sm: { onglet: "h-8 px-2.5 text-[12px]" },
  md: { onglet: "h-10 px-3 text-[13px]" },
  lg: { onglet: "h-12 px-4 text-[15px]" },
};

export function Onglets<V extends string = string>({
  options,
  valeur,
  onChange,
  style = "ligne",
  taille = "md",
  pleineLargeur = false,
  nom,
  semantique = "onglets",
  className,
}: OngletsProps<V>) {
  const id = useId();
  const reduit = useReducedMotion();
  const segments = style === "segments";
  const roles =
    semantique === "choix"
      ? { groupe: "radiogroup", item: "radio", actif: "aria-checked" as const }
      : { groupe: "tablist", item: "tab", actif: "aria-selected" as const };
  const transition = reduit ? { duration: 0 } : { type: "spring" as const, stiffness: 520, damping: 42, mass: 0.8 };

  function auClavier(e: KeyboardEvent<HTMLDivElement>) {
    const i = options.findIndex((o) => o.valeur === valeur);
    const n = options.length;
    const pas = (depuis: number, sens: 1 | -1) => {
      for (let k = 1; k <= n; k++) {
        const j = (depuis + sens * k + n * k) % n;
        if (!options[j].desactive) return j;
      }
      return depuis;
    };
    let j = i;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") j = pas(i, 1);
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") j = pas(i, -1);
    else if (e.key === "Home") j = pas(-1, 1);
    else if (e.key === "End") j = pas(0, -1);
    else return;
    e.preventDefault();
    const o = options[j];
    if (o && o.valeur !== valeur) onChange(o.valeur);
    e.currentTarget.querySelectorAll<HTMLButtonElement>(`[role=${roles.item}]`)[j]?.focus();
  }

  // `og-defile` : sur un écran trop étroit (téléphone), la rangée DÉFILE à l'horizontale, sans
  // barre, au lieu de déborder de la page (passe téléphone du 01-11-2026).
  return (
    <div
      data-style={style}
      className={cn("og-defile", segments && !pleineLargeur ? "inline-block align-middle" : "w-full", className)}
    >
    <div
      role={roles.groupe}
      aria-label={nom}
      onKeyDown={auClavier}
      className={cn(
        segments ? cn("og-rail lc-pilule inline-flex items-center", SEGMENTS[taille].rail) : "og-ligne flex items-end gap-1",
        pleineLargeur && "flex w-full",
      )}
    >
      {options.map((o) => {
        const actif = o.valeur === valeur;
        return (
          <button
            key={o.valeur}
            type="button"
            role={roles.item}
            {...{ [roles.actif]: actif }}
            aria-disabled={o.desactive || undefined}
            tabIndex={actif ? 0 : -1}
            onClick={() => {
              if (!actif && !o.desactive) onChange(o.valeur);
            }}
            className={cn(
              "relative isolate inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-semibold outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-line-strong",
              segments ? cn("lc-pilule", SEGMENTS[taille].segment) : cn("-mb-px", LIGNE[taille].onglet),
              pleineLargeur && "flex-1",
              o.desactive && "cursor-not-allowed opacity-40",
              actif ? (segments ? "text-canvas" : "text-fg") : "text-muted hover:text-fg",
            )}
          >
            {actif ? (
              segments ? (
                <motion.span layoutId={`${id}-actif`} className="og-actif lc-pilule absolute inset-0 -z-10" transition={transition} />
              ) : (
                <motion.span layoutId={`${id}-actif`} className="og-trait absolute inset-x-2 -bottom-px h-0.5" transition={transition} />
              )
            ) : null}
            {o.icone ? <span className="[&>svg]:h-[1.1em] [&>svg]:w-[1.1em]">{o.icone}</span> : null}
            {o.etiquette}
            {o.compte !== undefined ? <span className="og-compte lc-pilule">{o.compte}</span> : null}
            {o.suite ? <span className="inline-flex items-center gap-1">{o.suite}</span> : null}
          </button>
        );
      })}
    </div>
    </div>
  );
}
